// Le logement pendant la vie active : rester chez ses parents finit par peser, et on peut quitter le nid.
import { HOUSING } from '../content/paths';
import type { ExpenseId, GameState, HousingId } from '../engine/state';
import { fmtEur } from '../engine/format';
import { DAYS_PER_YEAR, addYearsISO } from '../engine/time';
import { addJournal } from './actions';
import { changeSubscription, findSubscription, hasSubscription, subscribe } from './subscriptions';

export const PARENTS_STAY = {
  /** Jusqu'à 21 ans, vivre chez ses parents ne pèse pas sur le moral… */
  fromAge: 21,
  /** …ensuite, la cible du bien-être baisse de 4 points par an… */
  perYear: 4,
  /** …jusqu'à 20 points au plus (à 26 ans). */
  max: 20,
};

export const COLOC_STAY = {
  /** Jusqu'à 27 ans, la colocation ne pèse pas sur le moral… */
  fromAge: 27,
  /** …ensuite, la cible du bien-être baisse de 2 points par an… */
  perYear: 2,
  /** …jusqu'à 15 points au plus (vers 34 ans). */
  max: 15,
};

/** Ce qu'on paie pour s'installer, en plus du premier mois de loyer versé en dépôt de garantie : le déménagement et les premiers meubles… */
const SETUP_COST: Record<'coloc' | 'studio', number> = { coloc: 150, studio: 300 };
/** …et les frais d'agence, plafonnés par la loi. */
const AGENCY_FEES: Record<'coloc' | 'studio', number> = { coloc: 200, studio: 300 };

/** Le jour de jeu d'un anniversaire (la partie commence l'été des 18 ans). */
function birthdayDay(state: GameState, age: number): number {
  const birthday = new Date(`${addYearsISO(state.birthDate, age)}T00:00:00`);
  const start = new Date(`${state.startDate}T00:00:00`);
  return Math.round((birthday.getTime() - start.getTime()) / 86_400_000);
}

/** Ce que coûte au moral le fait de vivre encore chez ses parents : rien avant 21 ans, puis de plus en plus. */
export function parentsMalus(state: GameState): number {
  if (state.housing !== 'parents') return 0;
  const years = (state.day - birthdayDay(state, PARENTS_STAY.fromAge)) / DAYS_PER_YEAR;
  return Math.min(PARENTS_STAY.max, Math.max(0, years * PARENTS_STAY.perYear));
}

/** Ce que coûte au moral une colocation qui dure : rien avant 27 ans, puis de plus en plus. */
export function colocMalus(state: GameState): number {
  if (state.housing !== 'coloc') return 0;
  const years = (state.day - birthdayDay(state, COLOC_STAY.fromAge)) / DAYS_PER_YEAR;
  return Math.min(COLOC_STAY.max, Math.max(0, years * COLOC_STAY.perYear));
}

/** La tuile de la colocation s'affiche un an avant que le malus ne commence. */
export function colocWarning(state: GameState): boolean {
  return state.housing === 'coloc' && state.day >= birthdayDay(state, COLOC_STAY.fromAge - 1);
}

/** On peut quitter le nid pendant les années d'études comme dans la vie active. */
export function canMoveOut(state: GameState): boolean {
  if (state.housing !== 'parents') return false;
  return state.stage !== 'prologue' || state.prologueStep === 'years';
}

/** Le loyer d'un logement, au niveau des prix du moment. */
export function rentOf(state: GameState, housing: HousingId): number {
  return Math.round(HOUSING[housing].rent * state.priceLevel);
}

/** S'installer : un mois de loyer en dépôt de garantie, les frais d'agence, le déménagement et les premiers meubles. */
export function moveOutCost(state: GameState, housing: 'coloc' | 'studio'): number {
  return rentOf(state, housing) + Math.round((AGENCY_FEES[housing] + SETUP_COST[housing]) * state.priceLevel);
}

/** Ce que coûte un logement chaque mois, tout compris : loyer, charges, électricité, assurance et box internet. */
export function housingMonthly(state: GameState, housing: HousingId): number {
  const def = HOUSING[housing];
  return Math.round((def.rent + def.charges + def.energy + def.insurance + def.internet) * state.priceLevel);
}

function setExpense(state: GameState, id: ExpenseId, amount: number): void {
  const expense = state.expenses[id];
  if (expense) expense.amount = amount;
  else state.expenses[id] = { amount, examined: 0, optimized: false };
}

/** La box internet d'un logement à soi rejoint les abonnements : entière en studio, partagée en colocation. */
export function connectInternet(state: GameState, housing: HousingId): void {
  const monthly = HOUSING[housing].internet;
  if (monthly <= 0 || hasSubscription(state, 'box_internet')) return;
  const amount = Math.round(monthly * state.priceLevel);
  const shared = housing === 'coloc';
  subscribe(state, 'box_internet', amount, { shared });
  state.flags = { ...state.flags, boxInternet: true };
  addJournal(state, shared ? 'journal.boxInternetColoc' : 'journal.boxInternet', { amount: fmtEur(amount) });
}

/** Les frais d'un logement à soi, au niveau des prix du moment : charges et eau, électricité et chauffage, assurance. */
function setHousingFees(state: GameState, housing: HousingId): void {
  const def = HOUSING[housing];
  if (def.charges > 0) setExpense(state, 'charges', Math.round(def.charges * state.priceLevel));
  if (def.energy > 0) setExpense(state, 'energie', Math.round(def.energy * state.priceLevel));
  if (def.insurance > 0) setExpense(state, 'assurance', Math.round(def.insurance * state.priceLevel));
  state.flags = { ...state.flags, fraisHabitation: true };
}

/** Le nouveau logement : son loyer et ses frais remplacent la participation aux frais chez les parents. */
export function moveOut(state: GameState, housing: 'coloc' | 'studio'): void {
  state.housing = housing;
  setExpense(state, 'logement', rentOf(state, housing));
  setHousingFees(state, housing);
  connectInternet(state, housing);
}

/** S'installer en studio depuis la colocation : le dépôt de garantie de la chambre revient. */
export function studioSwitchCost(state: GameState): number {
  return Math.max(0, moveOutCost(state, 'studio') - rentOf(state, 'coloc'));
}

/** Quitter la colocation pour un studio : nouveaux loyer et frais, la box internet passe à son nom. */
export function moveToStudio(state: GameState): void {
  state.housing = 'studio';
  setExpense(state, 'logement', rentOf(state, 'studio'));
  setHousingFees(state, 'studio');
  const box = findSubscription(state, 'box_internet');
  if (box) {
    // Le prix entier remplace la part de colocation, et la ligne « Abonnements » suit.
    changeSubscription(state, 'box_internet', Math.round(HOUSING.studio.internet * state.priceLevel) - box.amount);
    box.shared = false;
  } else {
    connectInternet(state, 'studio');
  }
}

/**
 * Les anciennes parties hors de chez les parents : les charges et l'électricité n'existaient pas,
 * et l'assurance du studio coûtait 15 € au lieu de 25 €. Elles arrivent une fois, au prix du moment.
 */
export function addHousingFees(state: GameState): void {
  if (!state.housing || state.housing === 'parents' || state.flags.fraisHabitation) return;
  const before = Object.values(state.expenses).reduce((sum, expense) => sum + (expense?.amount ?? 0), 0);
  const def = HOUSING[state.housing];
  // L'assurance garde une éventuelle économie déjà trouvée : elle monte dans la même proportion.
  const insurance = state.expenses.assurance;
  if (state.housing === 'studio' && insurance) insurance.amount = Math.round((insurance.amount * def.insurance) / 15);
  if (!state.expenses.charges) setExpense(state, 'charges', Math.round(def.charges * state.priceLevel));
  if (!state.expenses.energie) setExpense(state, 'energie', Math.round(def.energy * state.priceLevel));
  state.flags = { ...state.flags, fraisHabitation: true };
  const after = Object.values(state.expenses).reduce((sum, expense) => sum + (expense?.amount ?? 0), 0);
  addJournal(state, 'journal.habitationFees', { amount: fmtEur(after - before) });
}
